import { nullable, z } from 'zod';
import { AllCouponCodes } from '~/components/Referral/constants/campaigns-id';

export interface IReferralCampaign {
    _id: string;
    _short_url: string;
    _personal_invite_message: string;
    name: string;
    description: string;
    payout_terms: string;
    title: string;
    campaign_uid: AllCouponCodes;
    discount_amount_text: string;
    discount_duration: string;
    max_commission_amount: number;
    _selected_coupon_code: string;
}

const StatusUpdates = z.object({
    invited_at: nullableDate(),
    signed_up_at: nullableDate(),
    member_at: nullableDate(),
    not_eligible_at: nullableDate(),
    eligible_at: nullableDate(),
    paid_at: nullableDate()
});

export const REFERRALS_STATUS_OPTIONS = [
    'invited',
    'signed_up',
    'member',
    'eligible',
    'paid',
    'not_eligible'
] as const;

const ReferralSource = z.enum([
    'link',
    'email',
    'facebook',
    'x_social',
    'linkedin',
    'template',
    'copy',
    'other',
    'suggestion',
    'promo_code_admin',
    'promo_code'
]);

const IneligibilityReason = z.enum([
    'paused_membership',
    'terminated_membership',
    'referrer_terminated_membership',
    'registered_without_referral',
    'registered_using_different_referral',
    'invitation_expired',
    'signup_lock_window_expired',
    'canceled',
    'cooldown_period_subscription',
    'subscribed_without_referral_code',
    'other',
    'coupon_site',
    'fraud',
    'disabled',
    'ineligible'
]);

export const ReferralStatus = z.enum(REFERRALS_STATUS_OPTIONS);
export type TReferralStatus = z.infer<typeof ReferralStatus>;

function nullableDate() {
    return nullable(z.string())
        .optional()
        .transform(date => (typeof date === 'string' ? new Date(date) : null));
}

export const Referral = z.object({
    _id: z.string(),
    referred_user_email: z.string(),
    status: ReferralStatus,
    commission_amount: nullable(z.number()).optional(),
    trial_start_date: nullableDate(),
    invited_at_date: nullableDate(),
    last_reminder_timestamp: nullableDate(),
    subscribed_at_date: nullableDate(),
    source: ReferralSource,
    last_activity: nullableDate(),
    ineligibility_reason: nullable(IneligibilityReason).optional(),
    active_pricing_plan_type: nullable(z.string()).optional(),
    status_updates: nullable(StatusUpdates).optional()
});

export type TReferral = z.infer<typeof Referral>;
export type TReferralStatusUpdates = TReferral['status_updates'];

export type ReferUserParams = {
    userId: string;
    emails: string[];
    message: string;
    subject: string;
};

export type FetchUserReferralsParams = {
    limit: number;
    skip: number;
    search?: string;
    startDate?: string;
    sortDirection?: 'asc' | 'desc';
    statuses?: TReferralStatus[];
};

export type SendReminderParams = {
    userId: string;
    referralId: string;
};
export type GetReferralSuggestionsParams = {
    userId: string;
    limit: number;
    page: number;
};

export type CreateReferralParams = {
    referredEmail: string;
    referrerEmail: string;
};

export type TReferralInviteSource = z.infer<typeof ReferralSource>;
